import { useCallback, useEffect, useState } from "react";
import { Users, RefreshCw, Download } from "lucide-react";
import { toast } from "sonner";
import { api, errCode, errMsg, fmtCid, fmtDate } from "@/lib/api";
import { Panel, Tag, Spinner, Alert } from "@/components/common";
import AdsData from "@/components/settings/AdsData";

const TITLES = { access_level: "Google Cloud project access level", quota_exhausted: "Google Ads API daily limit reached", quota_reserve: "Paused to protect the API quota" };

const ApiError = ({ error }) =>
  error ? (
    <Alert testid="google-ads-error" tone={TITLES[error.code] ? "amber" : "red"} title={TITLES[error.code] || "Google Ads error"}>
      {error.message}
    </Alert>
  ) : null;

export default function AccountPanel({ status, refreshStatus, snapshot, setSnapshot, range, setRange }) {
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [cachedAt, setCachedAt] = useState(null);
  const selected = status.selected_account;

  const loadAccounts = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/google-ads/accounts", { params: refresh ? { refresh: true } : {} });
      setAccounts(data.accounts);
      setCachedAt(data.cached_at);
      if (data.quota_note) setError({ code: "quota_exhausted", message: `${data.quota_note} Showing the saved account list.` });
    } catch (e) {
      setError({ code: errCode(e), message: errMsg(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const pick = async (cid) => {
    setError(null);
    try {
      await api.put("/google-ads/account", { customer_id: cid });
      setSnapshot(null);
      toast.success("Account selected");
      refreshStatus();
    } catch (e) {
      setError({ code: errCode(e), message: errMsg(e) });
    }
  };

  const sync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const { data } = await api.post("/google-ads/sync");
      setSnapshot(data);
      toast.success("Pulled last 30 days from Google Ads");
    } catch (e) {
      setError({ code: errCode(e), message: errMsg(e) });
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-6">
      <Panel title="Accounts under your manager" icon={Users} testid="accounts-panel"
        right={<button data-testid="reload-accounts-button" onClick={() => loadAccounts(true)} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs"><RefreshCw className="h-3.5 w-3.5" /> Reload</button>}>
        <div className="space-y-4">
          <ApiError error={error} />
          {loading && <Spinner />}
          {cachedAt && <p className="font-mono text-[11px] text-slate-500" data-testid="accounts-cached-at">Saved list from {fmtDate(cachedAt)} (Reload uses 1 API operation)</p>}
          {accounts && accounts.length === 0 && <p className="text-sm text-slate-400">No accounts are linked under this manager account.</p>}
          {accounts && accounts.length > 0 && (
            <div className="divide-y divide-[#1e293b] rounded-md border border-[#1e293b]" data-testid="accounts-list">
              {accounts.map((a) => {
                const isSel = selected?.customer_id === a.customer_id;
                return (
                  <div key={a.customer_id} data-testid={`account-row-${a.customer_id}`} className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 transition-colors ${isSel ? "bg-emerald-500/5" : "hover:bg-[#162036]"}`}>
                    <div>
                      <div className="text-sm font-medium text-slate-100">{a.name}</div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-2 font-mono text-xs text-slate-500">
                        {fmtCid(a.customer_id)} · {a.currency} · {a.status}
                        {a.manager && <Tag tone="cyan">Manager</Tag>}
                        {a.test_account && <Tag tone="amber">Test account</Tag>}
                      </div>
                    </div>
                    {a.manager ? <span className="text-xs text-slate-600">manager account</span> : isSel ? <Tag tone="emerald">Selected</Tag> : (
                      <button data-testid={`select-account-${a.customer_id}`} onClick={() => pick(a.customer_id)} className="cc-btn cc-btn-ghost px-3 py-1.5 text-xs">Select</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Panel>

      {selected && (
        <Panel title={`Performance · ${selected.name}`} icon={Download} testid="ads-data-panel"
          right={
            <div className="flex items-center gap-3">
              {snapshot && <span className="font-mono text-xs text-slate-500">Pulled {fmtDate(snapshot.pulled_at)}</span>}
              <button data-testid="sync-ads-data-button" onClick={sync} disabled={syncing} className="cc-btn cc-btn-primary px-3 py-1.5 text-xs">
                {syncing ? <Spinner className="h-3.5 w-3.5 text-[#03130c]" /> : <RefreshCw className="h-3.5 w-3.5" />} Pull last 30 days
              </button>
            </div>
          }>
          {snapshot ? <AdsData snapshot={snapshot} range={range} setRange={setRange} /> : <p data-testid="ads-data-empty" className="text-sm text-slate-400">No data pulled yet. Press “Pull last 30 days” to read campaigns, ad groups, keywords and search terms.</p>}
        </Panel>
      )}
    </div>
  );
}
